// ============================================================================
// HEPATOLOGY CONCEPTS (Phase L-6 — liver branch)
//
// specialtyId = 'hepatology'. Liver chemistry (LFT enzymes), liver diagnoses
// (cirrhosis / fatty liver / hepatitis / jaundice), liver documents, and a
// conservative red-flag set. NOT a hepatology ontology — a ReferralGate
// operational evidence library for referral intake. Luminal GI lives in
// gastroenterology.js.
//
// CLASSIFICATION (L-6 contract — docs/L-6-gastroenterology-hepatology-concept-library-contract.md):
//   • LIVE_LINKABLE (16): 5 LAB measurements, 5 FINDING findings, 3 LAB documents,
//       3 RED_FLAG red flags. All ride EXISTING groups → ZERO evidenceAutoLink/
//       matcher logic change.
//   • DATA_ONLY: present + governed, NO pattern, NOT in any group → inert.
//   • DELAYED_RISKY (not added): bare liver_disease, hepatobiliary-surgery /
//       emergency redirect concepts (no REDIRECT target type).
//
// COLLISION REUSE (do NOT duplicate): hematology `bilirubin`/`inr` stay DATA_ONLY
// (NO hematology change in L-6 — liver bilirubin/INR are a deliberate future
// shared-labs promotion gap); liver chemistry surfaces via ALT/AST/ALP/GGT/albumin
// + `abnormal_liver_function_tests` + `jaundice`. Reuse shared `pathology_report`
// (biopsy), `abnormal_imaging`, `prior_workup`, `weight_loss`/`fever`.
//
// PRECEDENCE: `liver_function_test_result` (document) uses pattern /liver function
// test/ ONLY (NOT bare /lfts/) so it does NOT shadow `abnormal_liver_function_tests`
// (finding) on "abnormal/deranged LFTs". 'LFTs' is kept as a matcher alias.
//
// ABBREVIATION SAFETY: ALT/AST/ALP/GGT are word-bounded (\balt\b etc. — distinctive
// in clinical LFT context; do not match "altered"/"fast"/etc.).
//
// L-7 NARROWING (one surgical recognition edit): `albumin` recognition changed from
// /albumin/ to /albumin(?!uria)/ so it no longer mis-binds "albuminuria"/
// "microalbuminuria"/"macroalbuminuria" (now owned by nephrology `albuminuria`),
// while preserving albumin / serum albumin / low albumin / albumin low /
// hypoalbuminemia. Concept definition + ownership (hepatology) are unchanged.
//
// RED-FLAG PROJECTION GAP (L-2..L-5): only positive/negative/uncertainty project to
// Pack maps → suppression-critical phrases live in negative/uncertainty here.
//
// PURE / Never-PHI. Concept records are pure JSON data; recognition is a separate
// code-level RegExp table (flags 'i' only → stateless).
// ============================================================================

import { makeConcept, CONCEPT_TYPES } from './conceptBuilder.js';

export const concepts = [
  // ══════════════════════════════════════════════════════════════════════════
  // LIVE — measurements (LAB group; number / nearby_value / units). LFT enzymes.
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'alanine_aminotransferase', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology',
    canonicalLabel: 'ALT', description: 'Alanine aminotransferase (liver enzyme).',
    aliases: ['ALT', 'alanine aminotransferase', 'SGPT'], abbreviations: ['ALT', 'SGPT'],
    preferredUnit: 'U/L', allowedUnits: ['U/L', 'IU/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'aspartate_aminotransferase', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology',
    canonicalLabel: 'AST', description: 'Aspartate aminotransferase (liver enzyme).',
    aliases: ['AST', 'aspartate aminotransferase', 'SGOT'], abbreviations: ['AST', 'SGOT'],
    preferredUnit: 'U/L', allowedUnits: ['U/L', 'IU/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'alkaline_phosphatase', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology',
    canonicalLabel: 'ALP', description: 'Alkaline phosphatase (liver / biliary enzyme).',
    aliases: ['ALP', 'alkaline phosphatase'], abbreviations: ['ALP'],
    preferredUnit: 'U/L', allowedUnits: ['U/L', 'IU/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'gamma_glutamyl_transferase', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology',
    canonicalLabel: 'GGT', description: 'Gamma-glutamyl transferase (liver / biliary enzyme).',
    aliases: ['GGT', 'gamma-glutamyl transferase', 'gamma GT'], abbreviations: ['GGT'],
    preferredUnit: 'U/L', allowedUnits: ['U/L', 'IU/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'albumin', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology',
    canonicalLabel: 'Albumin', description: 'Serum albumin (liver synthetic function).',
    aliases: ['albumin', 'serum albumin'],
    preferredUnit: 'g/dL', allowedUnits: ['g/dL', 'g/L'],
    valuePattern: 'numeric_near_label', requiresNearbyValue: true,
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // LIVE — findings (FINDING group; OPTION + FIELD)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'jaundice', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology',
    canonicalLabel: 'Jaundice', description: 'Jaundice / icterus.',
    aliases: ['jaundice', 'icterus', 'jaundiced'],
    preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'], negativePhrases: ['no jaundice', 'not jaundiced'],
  }),
  makeConcept({
    conceptId: 'cirrhosis', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology',
    canonicalLabel: 'Cirrhosis', description: 'Liver cirrhosis.',
    aliases: ['cirrhosis', 'cirrhotic'],
    preferredDocumentTypes: ['IMAGING_REPORT', 'CLINICAL_NOTE', 'CONSULT_NOTE'], negativePhrases: ['no cirrhosis'],
  }),
  makeConcept({
    conceptId: 'fatty_liver', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology',
    canonicalLabel: 'Fatty liver', description: 'Fatty liver / hepatic steatosis (NAFLD/NASH).',
    aliases: ['fatty liver', 'hepatic steatosis', 'NAFLD', 'NASH', 'steatosis'], abbreviations: ['NAFLD', 'NASH'],
    preferredDocumentTypes: ['IMAGING_REPORT', 'CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'hepatitis', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology',
    canonicalLabel: 'Hepatitis', description: 'Hepatitis (viral / other).',
    aliases: ['hepatitis'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'REFERRAL_LETTER', 'CONSULT_NOTE'],
  }),
  makeConcept({
    conceptId: 'abnormal_liver_function_tests', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology',
    canonicalLabel: 'Abnormal liver function tests', description: 'Abnormal / deranged LFTs.',
    aliases: ['abnormal LFTs', 'deranged LFTs', 'abnormal liver enzymes', 'abnormal liver function tests'], abbreviations: ['LFTs'],
    preferredDocumentTypes: ['LAB_RESULT', 'CLINICAL_NOTE', 'REFERRAL_LETTER'], negativePhrases: ['normal LFTs', 'liver function normal'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // LIVE — documents (LAB group; alias binding; canonical doc types; no enum change)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'liver_function_test_result', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hepatology',
    canonicalLabel: 'Liver function test result', description: 'LFT panel result (LAB_RESULT).',
    aliases: ['liver function test', 'liver function tests', 'LFTs', 'LFT result'], abbreviations: ['LFTs'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'fibroscan_report', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hepatology',
    canonicalLabel: 'Fibroscan report', description: 'Fibroscan / transient elastography report (OTHER).',
    aliases: ['fibroscan', 'transient elastography', 'liver elastography'],
    preferredDocumentTypes: ['OTHER'], acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),
  makeConcept({
    conceptId: 'hepatitis_serology_result', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hepatology',
    canonicalLabel: 'Hepatitis serology result', description: 'Hepatitis serology result (LAB_RESULT).',
    aliases: ['hepatitis serology', 'hepatitis B serology', 'hepatitis C serology', 'HBsAg'], abbreviations: ['HBsAg'],
    preferredDocumentTypes: ['LAB_RESULT'], acceptableDocumentTypes: ['CLINICAL_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // LIVE — red flags (RED_FLAG group, positive_only). Suppression in negative/
  // uncertainty (projection gap).
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'acute_liver_failure', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hepatology',
    canonicalLabel: 'Acute liver failure', description: 'Acute / fulminant liver failure.',
    positivePhrases: ['acute liver failure', 'fulminant hepatic failure', 'coagulopathy with encephalopathy', 'jaundice with confusion'],
    negativePhrases: ['no encephalopathy', 'stable chronic liver disease', 'liver function improving'],
    uncertaintyPhrases: ['possible acute liver injury', 'abnormal LFTs without failure', 'mild confusion unrelated', 'chronic cirrhosis baseline'],
    excludedPhrases: ['chronic cirrhosis baseline', 'remote hepatitis history'],
    commonFalseMatches: ['chronic liver disease'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'DISCHARGE_SUMMARY', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'cholangitis', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hepatology',
    canonicalLabel: 'Cholangitis', description: 'Ascending cholangitis / biliary sepsis.',
    positivePhrases: ['ascending cholangitis', 'cholangitis', 'fever with jaundice', 'biliary sepsis', 'jaundice with right upper quadrant pain'],
    negativePhrases: ['no fever', 'no jaundice', 'cholangitis ruled out'],
    uncertaintyPhrases: ['possible cholangitis', 'query biliary obstruction', 'mild LFT derangement', 'asymptomatic gallstones'],
    excludedPhrases: ['asymptomatic gallstones', 'resolved biliary colic'],
    commonFalseMatches: ['gallstones without symptoms'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'DISCHARGE_SUMMARY', 'REFERRAL_LETTER'],
  }),
  makeConcept({
    conceptId: 'decompensated_cirrhosis', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hepatology',
    canonicalLabel: 'Decompensated cirrhosis', description: 'Decompensated cirrhosis / liver disease.',
    positivePhrases: ['decompensated cirrhosis', 'decompensated liver disease', 'hepatic decompensation', 'variceal bleeding', 'new ascites with cirrhosis', 'hepatic encephalopathy'],
    negativePhrases: ['compensated cirrhosis', 'stable liver disease', 'no decompensation', 'stable chronic liver disease'],
    uncertaintyPhrases: ['possible decompensation', 'mild ascites', 'remote decompensation', 'chronic cirrhosis baseline'],
    excludedPhrases: ['stable compensated cirrhosis', 'chronic cirrhosis baseline'],
    commonFalseMatches: ['compensated chronic liver disease'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'DISCHARGE_SUMMARY', 'CONSULT_NOTE'],
  }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — measurements (no pattern, no group → inert)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'afp', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology', canonicalLabel: 'AFP', description: 'Alpha-fetoprotein (HCC surveillance).', aliases: ['AFP', 'alpha-fetoprotein', 'alpha fetoprotein'], abbreviations: ['AFP'], preferredUnit: 'ng/mL', allowedUnits: ['ng/mL'], requiresNearbyValue: true, preferredDocumentTypes: ['LAB_RESULT'] }),
  makeConcept({ conceptId: 'hepatitis_b_result', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology', canonicalLabel: 'Hepatitis B result', description: 'Hepatitis B serology result.', aliases: ['hepatitis B', 'HBsAg', 'hep B'], requiresNearbyValue: false, preferredDocumentTypes: ['LAB_RESULT'] }),
  makeConcept({ conceptId: 'hepatitis_c_result', conceptType: CONCEPT_TYPES.MEASUREMENT, specialtyId: 'hepatology', canonicalLabel: 'Hepatitis C result', description: 'Hepatitis C serology result.', aliases: ['hepatitis C', 'HCV', 'hep C'], requiresNearbyValue: false, preferredDocumentTypes: ['LAB_RESULT'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — findings
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'liver_lesion', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology', canonicalLabel: 'Liver lesion', description: 'Liver lesion / mass on imaging.', aliases: ['liver lesion', 'liver mass', 'hepatic lesion'], preferredDocumentTypes: ['IMAGING_REPORT', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'ascites', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology', canonicalLabel: 'Ascites', description: 'Ascites.', aliases: ['ascites', 'abdominal distension with fluid'], preferredDocumentTypes: ['IMAGING_REPORT', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'pruritus', conceptType: CONCEPT_TYPES.SYMPTOM, specialtyId: 'hepatology', canonicalLabel: 'Pruritus', description: 'Pruritus / itching (cholestasis).', aliases: ['pruritus', 'itching', 'generalised itch'], preferredDocumentTypes: ['REFERRAL_LETTER', 'CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'pancreatic_cyst', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology', canonicalLabel: 'Pancreatic cyst', description: 'Pancreatic cyst on imaging.', aliases: ['pancreatic cyst', 'pancreatic lesion'], preferredDocumentTypes: ['IMAGING_REPORT', 'CLINICAL_NOTE'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — documents (no enum expansion; biopsy reuses shared pathology_report)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'mri_liver_report', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hepatology', canonicalLabel: 'MRI liver report', description: 'MRI liver report (IMAGING_REPORT).', aliases: ['MRI liver', 'liver MRI', 'MRI liver report'], preferredDocumentTypes: ['IMAGING_REPORT'], acceptableDocumentTypes: ['CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'mrcp_report', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hepatology', canonicalLabel: 'MRCP report', description: 'MRCP report (IMAGING_REPORT).', aliases: ['MRCP', 'magnetic resonance cholangiopancreatography'], abbreviations: ['MRCP'], preferredDocumentTypes: ['IMAGING_REPORT'], acceptableDocumentTypes: ['CLINICAL_NOTE'] }),
  makeConcept({ conceptId: 'liver_biopsy_report', conceptType: CONCEPT_TYPES.DOCUMENT, specialtyId: 'hepatology', canonicalLabel: 'Liver biopsy report', description: 'Liver biopsy report (OTHER; reuse shared pathology_report where relevant).', aliases: ['liver biopsy', 'liver biopsy report'], preferredDocumentTypes: ['OTHER'], acceptableDocumentTypes: ['CLINICAL_NOTE'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — prior workup (findings)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'prior_liver_ultrasound', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology', canonicalLabel: 'Prior liver ultrasound', description: 'Prior liver ultrasound performed.', aliases: ['prior liver ultrasound', 'previous liver ultrasound'], preferredDocumentTypes: ['IMAGING_REPORT', 'REFERRAL_LETTER'] }),
  makeConcept({ conceptId: 'prior_hepatology_review', conceptType: CONCEPT_TYPES.FINDING, specialtyId: 'hepatology', canonicalLabel: 'Prior hepatology review', description: 'Previously seen by hepatology.', aliases: ['prior hepatology review', 'previously seen by hepatology', 'prior liver clinic'], preferredDocumentTypes: ['CONSULT_NOTE', 'REFERRAL_LETTER'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — medication / exposure (REUSE hematology `alcohol_exposure` — not duplicated)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({ conceptId: 'hepatotoxic_medication_exposure', conceptType: CONCEPT_TYPES.MEDICATION_EXPOSURE, specialtyId: 'hepatology', canonicalLabel: 'Hepatotoxic medication exposure', description: 'Hepatotoxic drug exposure.', aliases: ['hepatotoxic medication', 'drug-induced liver injury', 'DILI'], abbreviations: ['DILI'], preferredDocumentTypes: ['MEDICATION_LIST', 'CLINICAL_NOTE'] }),

  // ══════════════════════════════════════════════════════════════════════════
  // DATA_ONLY — red flags (fully boundaried, NOT recognition-reachable)
  // ══════════════════════════════════════════════════════════════════════════
  makeConcept({
    conceptId: 'jaundice_with_systemic_symptoms', conceptType: CONCEPT_TYPES.RED_FLAG, specialtyId: 'hepatology',
    canonicalLabel: 'Jaundice with systemic symptoms', description: 'Jaundice with fever / systemic features.',
    positivePhrases: ['jaundice with fever', 'jaundice with systemic symptoms', 'jaundice with rigors', 'painful jaundice with fever'],
    negativePhrases: ['no jaundice', 'no fever', 'afebrile'],
    uncertaintyPhrases: ['mild jaundice', 'possible jaundice', 'isolated bilirubin rise'],
    excludedPhrases: ['chronic stable jaundice', 'Gilbert syndrome'], commonFalseMatches: ['Gilberts'],
    preferredDocumentTypes: ['CLINICAL_NOTE', 'DISCHARGE_SUMMARY', 'REFERRAL_LETTER'],
  }),
];

// ============================================================================
// DELAYED_RISKY — intentionally NOT added in L-6 (documented for governance).
// Bare liver_disease (too broad), hepatobiliary-surgery / emergency redirect
// concepts (no REDIRECT target type — pathway-engine logic).
// ============================================================================
export const DELAYED_RISKY_HEPATOLOGY_CONCEPT_IDS = Object.freeze([
  'liver_disease', 'hepatobiliary_surgery_primary', 'emergency_cholangitis',
  'oncology_primary_suspected_cancer',
]);

// ============================================================================
// HEPATOLOGY RECOGNITION PATTERNS (code-level RegExp, keyed by conceptId).
// EXACTLY the 16 LIVE concepts. ALT/AST/ALP/GGT word-bounded. `liver_function_
// test_result` uses /liver function test/ ONLY (NOT bare /lfts/) so it does not
// shadow `abnormal_liver_function_tests`. DATA_ONLY concepts have NO pattern → inert.
// ============================================================================
export const recognitionPatterns = {
  // LAB measurements (LFT enzymes)
  alanine_aminotransferase:   [/\balt\b/i, /alanine (amino)?transferase/i],
  aspartate_aminotransferase: [/\bast\b/i, /aspartate (amino)?transferase/i],
  alkaline_phosphatase:       [/\balp\b/i, /alkaline phosphatase/i],
  gamma_glutamyl_transferase: [/\bggt\b/i, /gamma.?glutamyl/i],
  albumin:                    [/albumin(?!uria)/i],   // L-7: negative lookahead so it does NOT mis-bind albuminuria/micro-/macro-albuminuria (nephrology owns those); preserves albumin/serum albumin/low albumin/albumin low/hypoalbuminemia

  // LAB documents
  liver_function_test_result: [/liver function test/i],   // NOT bare /lfts/ — avoids shadowing the finding
  fibroscan_report:           [/fibroscan/i, /transient elastography/i],
  hepatitis_serology_result:  [/hepatitis serology/i, /\bhbsag\b/i, /hepatitis [bc] (serology|result)/i],

  // FINDING findings
  jaundice:                       [/jaundice/i, /icterus/i],
  cirrhosis:                      [/cirrhosis/i],
  fatty_liver:                    [/fatty liver/i, /steatosis/i, /\bnafld\b/i, /\bnash\b/i],
  hepatitis:                      [/hepatitis/i],
  abnormal_liver_function_tests:  [/abnormal (liver function|liver enzyme|lfts?)/i, /deranged lfts?/i],

  // RED_FLAG red flags
  acute_liver_failure:    [/acute liver failure/i, /fulminant hepatic failure/i, /fulminant liver failure/i],
  cholangitis:            [/cholangitis/i, /biliary sepsis/i],
  decompensated_cirrhosis:[/decompensated cirrhosis/i, /decompensated liver disease/i, /hepatic decompensation/i],
};
